/**
 * Bounded L1 production-neighborhood finish layer.
 *
 * This is non-colliding architectural dressing around the authoritative desk
 * zones: acoustic ceiling baffles break up the exposed slab and warm panels
 * give the blank west wall a human-scale rhythm. Desk positions, structural
 * geometry, room ownership, and circulation remain owned by their existing
 * systems.
 */
import * as pc from 'playcanvas'
import { createRenderEntity } from './interiorShared'

export type HqProductionFinishKind = 'ceiling-baffle' | 'wall-panel'

export interface HqProductionFinishSpec {
  id: string
  kind: HqProductionFinishKind
  position: readonly [number, number, number]
  scale: readonly [number, number, number]
}

const L1_CEILING_Y = 8.52
const L1_WALL_PANEL_Y = 6.45

export const HQ_L1_PRODUCTION_FINISHES: readonly HqProductionFinishSpec[] = [
  ...([
    [-15, -9],
    [-9, -9],
    [-15, 9],
    [-9, 9],
  ] as const).flatMap(([x, z], podIndex) =>
    ([-0.66, 0.66] as const).map((offset, baffleIndex) => ({
      id: `hq-l1-production-ceiling-baffle-${podIndex + 1}-${baffleIndex + 1}`,
      kind: 'ceiling-baffle' as const,
      position: [x, L1_CEILING_Y, z + offset] as const,
      scale: [3.15, 0.1, 0.2] as const,
    })),
  ),
  ...([-11.7, -10.5, -9.3, 9.3, 10.5, 11.7] as const).map((z, index) => ({
    id: `hq-l1-production-wall-panel-${index + 1}`,
    kind: 'wall-panel' as const,
    // The west shell's interior face is x=-24. Keep the finish proud of it
    // without adding collision volume or stealing circulation.
    position: [-23.94, L1_WALL_PANEL_Y, z] as const,
    scale: [0.08, 2.6, 0.82] as const,
  })),
] as const

function finishMaterial(
  diffuse: readonly [number, number, number],
  gloss: number,
): pc.StandardMaterial {
  const material = new pc.StandardMaterial()
  material.diffuse = new pc.Color(...diffuse)
  material.gloss = gloss
  material.update()
  return material
}

/**
 * Build only render geometry. None of these elements registers a collider, so
 * the accepted 1.2 m routes and egress remain exactly as authored.
 */
export function buildHqL1ProductionFinish(
  parent: pc.Entity,
  batchGroupId?: number,
): void {
  const materials: Record<HqProductionFinishKind, pc.StandardMaterial> = {
    'ceiling-baffle': finishMaterial([0.42, 0.29, 0.19], 0.18),
    'wall-panel': finishMaterial([0.49, 0.35, 0.27], 0.12),
  }
  for (const spec of HQ_L1_PRODUCTION_FINISHES) {
    const entity = createRenderEntity({
      parent,
      name: spec.id,
      type: 'box',
      position: spec.position,
      scale: spec.scale,
      material: materials[spec.kind],
    })
    if (entity.render) {
      entity.render.castShadows = false
      if (batchGroupId !== undefined) entity.render.batchGroupId = batchGroupId
    }
  }
}
